import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import FunnelPlot from '../components/FunnelPlot';
import CostPlot from '../components/CostPlot';
import TimePlot from '../components/TimePlot';
import RecruitmentBox from '../components/boxes/RecruitmentBox';
import ScreeningBox from '../components/boxes/ScreeningBox';
import CostBox from '../components/boxes/CostBox';
import TimeBox from '../components/boxes/TimeBox';
import DragDropCSV from '../components/DragDropCSV';
import HistogramWithCutoffs from '../components/HistogramWithCutoffs';
import RocCurve from '../components/RocCurve';
import Papa from 'papaparse';
import InfoTooltip from '../components/InfoTooltip';
import { TooltipProvider } from '../providers/TooltipContext';
import CustomInput from '../components/CustomInput';

export const getJsonFromCsv = (csvFilePath) => {
    return new Promise((resolve, reject) => {
        Papa.parse(csvFilePath, {
            download: true,
            header: true,
            complete: (result) => {
                resolve(result.data);
            },
            error: (error) => {
                reject(error);
            }
        });
    });
};

const formatCohortsData = (data) => {
    const grouped = data.reduce((acc, assay) => {
        let groupId = `${assay.positivity_method} - ${assay.population} - ${assay.apoe} - ${assay.type}`;
        let populationGroup = acc.find(group => group.id === groupId);

        if (!populationGroup) {
            populationGroup = {
                id: groupId,
                label: assay.type,
                population: assay.population,
                apoe: assay.apoe,
                type: assay.type,
                positivity_method: assay.positivity_method,
                options: []
            };
            acc.push(populationGroup);
        }

        const newOption = {
            value: `${assay.positivity_method} - ${assay.population} - ${assay.apoe} - ${assay.type} - ${assay.biomarker} - ${assay.assay} - ${assay.cohort}`,
            label: `${assay.biomarker.toUpperCase()} ${assay.assay} (Sens: ${assay.sensitivity}, Spec: ${assay.specificity})`,
            sensitivity: assay.sensitivity,
            specificity: assay.specificity,
            population: assay.population,
            apoe: assay.apoe,
            type: assay.type,
            positivity_method: assay.positivity_method,
        };

        populationGroup.options.push(newOption);

        return acc;
    }, []);

    return grouped;
};

const formatPrevalenceData = (data) => {
    const grouped = data.reduce((acc, assay) => {
        let groupId = `${assay.positivity_method} - ${assay.population} - ${assay.apoe} - ${assay.type}`;
        let populationGroup = acc.find(group => group.id === groupId);

        if (!populationGroup) {
            populationGroup = {
                id: groupId,
                label: assay.type,
                population: assay.population,
                apoe: assay.apoe,
                type: assay.type,
                positivity_method: assay.positivity_method,
                options: []
            };
            acc.push(populationGroup);
        }

        const newOption = {
            value: `${assay.population} - ${assay.apoe} - ${assay.type}`,
            label: `${assay.positivity_method} (Prevalence: ${assay.prevalence})`,
            prevalence: assay.prevalence,
            population: assay.population,
            apoe: assay.apoe,
            type: assay.type,
            positivity_method: assay.positivity_method,
        };

        populationGroup.options.push(newOption);

        return acc;
    }, []);

    return grouped;
};

const csvFilePath = '/cohorts_summary.csv';
const csvPrevalenceFilePath = '/cohorts_prevalence.csv';

const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 12,
    fontWeight: '300',
    color: '#212529',
};

const formatGroupLabel = (data) => (
    <div style={groupStyles}>
        <span>{data.label}</span>
    </div>
);

const flattenOptions = (options, attributes) => {
    return options.reduce((acc, category) => {
        const flattenedOptions = (category.options || []).map(option => {
            // Dynamically build an option object based on the specified attributes
            return attributes.reduce((optionAcc, attr) => {
                optionAcc[attr] = option[attr];
                return optionAcc;
            }, { value: option.value, label: option.label });
        });
        return [...acc, ...flattenedOptions];
    }, []);
};

export function calculateSensitivitySpecificity(threshold, data) {
    const predictions = data.map(row => (row.value > threshold ? 1 : 0));
    const actual = data.map(row => row.positivity);

    const confusionMatrix = {
        truePositive: predictions.filter((pred, index) => pred === 1 && actual[index] === 1).length,
        falseNegative: predictions.filter((pred, index) => pred === 0 && actual[index] === 1).length,
        trueNegative: predictions.filter((pred, index) => pred === 0 && actual[index] === 0).length,
        falsePositive: predictions.filter((pred, index) => pred === 1 && actual[index] === 0).length,
    };

    const sensitivity = confusionMatrix.truePositive / (confusionMatrix.truePositive + confusionMatrix.falseNegative);
    const specificity = confusionMatrix.trueNegative / (confusionMatrix.trueNegative + confusionMatrix.falsePositive);

    return {
        sensitivity,
        specificity,
    };
}

export function calculateROC(data) {
    const thresholds = [...new Set(data.map(row => row.value))].sort((a, b) => a - b);

    const rocPoints = thresholds.map(threshold => {
        const { sensitivity, specificity } = calculateSensitivitySpecificity(threshold, data);
        return { threshold, sensitivity, specificity };
    });

    return rocPoints;
}

export function findOptimalThresholds(rocPoints, desiredSensitivity, desiredSpecificity) {
    const cutPointSens = rocPoints.reduce((prev, curr) => (Math.abs(curr.sensitivity - desiredSensitivity) < Math.abs(prev.sensitivity - desiredSensitivity) ? curr : prev)).threshold;
    const cutPointSpec = rocPoints.reduce((prev, curr) => (Math.abs(curr.specificity - desiredSpecificity) < Math.abs(prev.specificity - desiredSpecificity) ? curr : prev)).threshold;
    const cutPointYouden = rocPoints.reduce((prev, curr) => ((curr.sensitivity + curr.specificity - 1) > (prev.sensitivity + prev.specificity - 1) ? curr : prev)).threshold;

    return {
        cutPointSens,
        cutPointSpec,
        cutPointYouden,
    };
}

function PreScreening() {
    // Initialize with empty strings or default values instead of null
    const [sensitivity, setSensitivity] = useState('0.88');
    const [specificity, setSpecificity] = useState('0.86');
    const [prevalence, setPrevalence] = useState('0.55');
    const [finalTP, setFinalTP] = useState(1000);
    const [clinicalAssessmentCost, setClinicalAssessmentCost] = useState(1000);
    const [prescreeningCost, setPrescreeningCost] = useState(200);
    const [otherPrescreeningCost, setOtherPrescreeningCost] = useState(0);
    const [screeningCost, setScreeningCost] = useState(3000);
    const [selectedOptionAssay, setSelectedOptionAssay] = useState(null);
    const [selectedOptionPrevalence, setSelectedOptionPrevalence] = useState(null);
    const [selectedPopulation, setSelectedPopulation] = useState('CU');
    const [selectedApoe, setSelectedApoe] = useState('All');
    const [clinicalAssessmentTiming, setClinicalAssessmentTiming] = useState('before');

    const [dataFrame, setDataFrame] = useState(null);
    const [rocPoints, setRocPoints] = useState([]);
    const [optimalThresholds, setOptimalThresholds] = useState(null);
    const [desiredSensitivity, setDesiredSensitivity] = useState(0.95);
    const [desiredSpecificity, setDesiredSpecificity] = useState(0.95);

    const [optionsAssay, setOptionsAssay] = useState([]);
    const [optionsPrevalence, setOptionsPrevalence] = useState([]);

    // constructor 
    // useEffect(() => {
    //     getJsonFromCsv(csvFilePath)
    //         .then((data) => {
    //             const groupedData = formatCohortsData(data);
    //             setOptionsAssay(groupedData);
    //         })
    //         .catch((error) => console.error('Error loading CSV:', error));

    //     getJsonFromCsv(csvPrevalenceFilePath)
    //         .then((data) => {
    //             const groupedData = formatPrevalenceData(data);
    //             setOptionsPrevalence(groupedData);
    //         })
    //         .catch((error) => console.error('Error loading CSV:', error));
    // }, []);

    const handleDataFrameChange = (df) => {
        setDataFrame(df);
        handleThresholdCalculation(df);
    };

    const handleRocPointsChange = (rocPoints) => {
        setRocPoints(rocPoints);
        const thresholds = findOptimalThresholds(rocPoints, desiredSensitivity, desiredSpecificity);
        setOptimalThresholds(thresholds);
    };

    const handleThresholdCalculation = useCallback((data) => {
        const rocPoints = calculateROC(data);
        handleRocPointsChange(rocPoints);
    }, [desiredSensitivity, desiredSpecificity]);

    const handleDesiredSensitivityChange = (event) => {
        setDesiredSensitivity(parseFloat(event.target.value));
        recalculateThresholds();
    };

    const handleDesiredSpecificityChange = (event) => {
        setDesiredSpecificity(parseFloat(event.target.value));
        recalculateThresholds();
    };

    const recalculateThresholds = () => {
        if (rocPoints.length > 0) {
            const thresholds = findOptimalThresholds(rocPoints, desiredSensitivity, desiredSpecificity);
            setOptimalThresholds(thresholds);
        }
    };

    const handleThresholdSelection = (threshold) => {
        if (!dataFrame) return;
        const { sensitivity, specificity } = calculateSensitivitySpecificity(threshold, dataFrame);
        setSensitivity(sensitivity.toFixed(3));
        setSpecificity(specificity.toFixed(3));
    };

    // Add this helper function to get first available option
    const getFirstAvailableOption = (options, population, apoe, filterFunc) => {
        const filteredOptions = filterFunc(population, apoe);
        if (filteredOptions.length > 0 && filteredOptions[0].options.length > 0) {
            return filteredOptions[0].options[0];
        }
        return null;
    };
    
    // Add useEffect to automatically select first options when data is loaded
    useEffect(() => {
        if (optionsAssay.length > 0 && optionsPrevalence.length > 0 && !selectedOptionAssay && !selectedOptionPrevalence) {
            const firstAssayOption = getFirstAvailableOption(
                optionsAssay, 
                selectedPopulation, 
                selectedApoe, 
                getFilteredAssayOptions
            );
            
            const firstPrevalenceOption = getFirstAvailableOption(
                optionsPrevalence, 
                selectedPopulation, 
                selectedApoe, 
                getFilteredPrevalenceOptions
            );
            
            if (firstAssayOption) {
                handleSelectionChange(
                    firstAssayOption, 
                    setSelectedOptionAssay, 
                    optionsAssay, 
                    { sensitivity: setSensitivity, specificity: setSpecificity }
                );
            }
            
            if (firstPrevalenceOption) {
                handleSelectionChange(
                    firstPrevalenceOption, 
                    setSelectedOptionPrevalence, 
                    optionsPrevalence, 
                    { prevalence: setPrevalence }
                );
            }
        }
    }, [optionsAssay, optionsPrevalence, selectedPopulation, selectedApoe]);

    // Modify the existing useEffect that resets selections when population/APOE changes
    useEffect(() => {
        setSelectedOptionAssay(null);
        setSelectedOptionPrevalence(null);
        
        // Try to select first options available for the new selection
        const firstAssayOption = getFirstAvailableOption(
            optionsAssay, 
            selectedPopulation, 
            selectedApoe, 
            getFilteredAssayOptions
        );
        
        const firstPrevalenceOption = getFirstAvailableOption(
            optionsPrevalence, 
            selectedPopulation, 
            selectedApoe, 
            getFilteredPrevalenceOptions
        );
        
        if (firstAssayOption) {
            handleSelectionChange(
                firstAssayOption, 
                setSelectedOptionAssay, 
                optionsAssay, 
                { sensitivity: setSensitivity, specificity: setSpecificity }
            );
        }
        
        if (firstPrevalenceOption) {
            handleSelectionChange(
                firstPrevalenceOption, 
                setSelectedOptionPrevalence, 
                optionsPrevalence, 
                { prevalence: setPrevalence }
            );
        }
    }, [selectedPopulation, selectedApoe]);

    const handleSelectionChange = (selectedOption, setOption, options, setters) => {
        setOption(selectedOption);

        const flattenedOptions = flattenOptions(options, Object.keys(setters));

        const foundOption = flattenedOptions.find(option => option.value === selectedOption.value);

        if (foundOption) {
            Object.entries(setters).forEach(([attr, setter]) => {
                if (foundOption[attr] !== undefined) {
                    // Convert number values to string to avoid controlled/uncontrolled warnings
                    setter(foundOption[attr].toString());
                }
            });
        }
    };

    const populationOptions = ['CU', 'EAD+MCI'];

    const PopulationButton = ({ option, onSelect, isSelected }) => (
        <button
            type="button"
            className={`btn ${isSelected ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onSelect(option)}
        >
            {option}
        </button>
    );

    const apoeOptions = ['All', 'Carrier', 'Non-carrier'];

    const ApoeButton = ({ option, onSelect, isSelected }) => (
        <button
            type="button"
            className={`btn ${isSelected ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onSelect(option)}
        >
            {option}
        </button>
    );

    const SensitivitySpecificity = (threshold) => {
        const { sensitivity, specificity } = calculateSensitivitySpecificity(threshold, dataFrame);

        return (
            <h3>Sensitivity: {sensitivity.toFixed(3)}, Specificity: {specificity.toFixed(3)}</h3>
        );
    };

    const getFilteredAssayOptions = (selectedPopulation, selectedApoe) => {
        return optionsAssay
            .filter(category => category.population === selectedPopulation && category.apoe === selectedApoe && category.positivity_method === "Aβ PET")
            .map(category => ({
                label: category.label,
                options: category.options.map(option => ({
                    value: option.value,
                    label: `${option.label}`
                }))
            }))
            .filter(category => category.options.length > 0);
    };

    const getFilteredPrevalenceOptions = (selectedPopulation) => {
        return optionsPrevalence
            .filter(category => category.population === selectedPopulation && category.apoe === selectedApoe && category.positivity_method === "Aβ PET")
            .map(category => ({
                label: category.label,
                options: category.options.map(option => ({
                    value: option.value,
                    label: `${option.label}`
                }))
            }))
            .filter(category => category.options.length > 0);
    };

    const areAllVariablesNotNull = () => {
        return sensitivity !== '' &&
            specificity !== '' &&
            prevalence !== '' &&
            finalTP !== '' &&
            clinicalAssessmentCost !== '' &&
            prescreeningCost !== '' &&
            otherPrescreeningCost !== '' &&
            screeningCost !== '';
    };

    // Helper function to safely handle input changes
    const handleNumberInputChange = (setter) => (e) => {
        const value = e.target.value;
        // Allow empty string or convert to number
        setter(value === '' ? '' : Number(value));
    };

    return (
        <div>
            <h1 className='mb-5'>Reference charts of plasma p-tau for Clinical Trials</h1>
            {/*<div className="card p-3">
                <h3 className='mb-3'>Load pre-defined parameters <InfoTooltip tooltipTextKey="tooltip_pre_defined_parameters" /></h3>
                <div className="row pb-3">
                    <div className="col-md-3">
                        <label className="form-label">Population <InfoTooltip tooltipTextKey="tooltip_population" /></label>
                        <div className="btn-group d-flex">
                            {populationOptions.map(option => (
                                <PopulationButton
                                    key={option}
                                    option={option}
                                    onSelect={setSelectedPopulation}
                                    isSelected={selectedPopulation === option}
                                />
                            ))}
                        </div>
                    </div>
                    //{/*<div className="col-md-3">
                        <label className="form-label">APOE-ε4 status <InfoTooltip tooltipTextKey="tooltip_apoe_status" /></label>
                        <div className="btn-group d-flex">
                            {apoeOptions.map(option => (
                                <ApoeButton
                                    key={option}
                                    option={option}
                                    onSelect={setSelectedApoe}
                                    isSelected={selectedApoe === option}
                                />
                            ))}
                        </div>
                    </div>//}
                    <div className="col-md-6">
                        {selectedPopulation && (<div className='assay-group'>
                            <label className="form-label">Assay <InfoTooltip tooltipTextKey="tooltip_assay" /></label>
                            {selectComponent(
                                selectedOptionAssay,
                                (selectedOption) => handleSelectionChange(selectedOption, setSelectedOptionAssay, optionsAssay, { sensitivity: setSensitivity, specificity: setSpecificity }),
                                selectedPopulation,
                                selectedApoe,
                                getFilteredAssayOptions
                            )}
                        </div>)}
                    </div>
                    <div className="col-md-3">
                        {selectedPopulation && (<div>
                            <label className="form-label">Prevalence <InfoTooltip tooltipTextKey="tooltip_prevalence" /></label>
                            {selectComponent(
                                selectedOptionPrevalence,
                                (selectedOption) => handleSelectionChange(selectedOption, setSelectedOptionPrevalence, optionsPrevalence, { prevalence: setPrevalence }),
                                selectedPopulation,
                                selectedApoe,
                                getFilteredPrevalenceOptions
                            )}
                        </div>)}
                    </div>
                </div>
                <h3 className='mb-3'>Calculate parameters from data <InfoTooltip tooltipTextKey="tooltip_custom_parameters" /></h3>
                <div className="row">
                    <div className="col-md-3">
                        <button type="button" className="btn btn-primary btn-load-data" data-toggle="modal" data-target="#modalCustomData">
                            Load data
                        </button>
                    </div>
                </div>
            </div>*/}
            <div className="card p-3">
                <h3 className='mb-3'>Parameters</h3>
                <div className="row g-3">
                    <div className="col-md-6">
                        <h4 className='mb-3'>Prescreening test <InfoTooltip tooltipTextKey="tooltip_pre_screening_test" /></h4>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Sensitivity <InfoTooltip tooltipTextKey="tooltip_sensitivity" /></label>
                                <input className="form-control" type="number" 
                                    value={sensitivity} 
                                    onChange={handleNumberInputChange(setSensitivity)} 
                                    min="0" max="1" step="0.01" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Specificity <InfoTooltip tooltipTextKey="tooltip_specificity" /></label>
                                <input className="form-control" type="number" 
                                    value={specificity} 
                                    onChange={handleNumberInputChange(setSpecificity)} 
                                    min="0" max="1" step="0.01" />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <h4 className='mb-3'>Clinical trial <InfoTooltip tooltipTextKey="tooltip_clinical_trial_parameters" /></h4>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Prevalence <InfoTooltip tooltipTextKey="tooltip_prevalence" /></label>
                                <input className="form-control" type="number" 
                                    value={prevalence} 
                                    onChange={handleNumberInputChange(setPrevalence)} 
                                    min="0" max="1" step="0.01" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Final Sample Size <InfoTooltip tooltipTextKey="tooltip_final_sample_size" /></label>
                                <input className="form-control" type="number" 
                                    value={finalTP} 
                                    onChange={handleNumberInputChange(setFinalTP)} 
                                    min="1" step="1" />
                            </div>
                        </div>
                    </div>
                </div>
                <h4 className='mb-3 mt-3'>Cost <InfoTooltip tooltipTextKey="tooltip_cost_parameters" /></h4>
                <div className="row g-3">
                    <div className="col-md-3">
                        <label>Prescreening Cost <InfoTooltip tooltipTextKey="tooltip_pre_screening_cost" /></label>
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text">$</span>
                            </div>
                            <input className="form-control" type="number" 
                                value={prescreeningCost} 
                                onChange={handleNumberInputChange(setPrescreeningCost)} 
                                min="0" />
                        </div>
                    </div>

                    <div className="col-md-3">
                        <label>Clinical Assessment Cost <InfoTooltip tooltipTextKey="tooltip_clinical_assessment_cost" /></label>
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text">$</span>
                            </div>
                            <input className="form-control" type="number" 
                                value={clinicalAssessmentCost} 
                                onChange={handleNumberInputChange(setClinicalAssessmentCost)} 
                                min="0" />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <label>Screening Cost <InfoTooltip tooltipTextKey="tooltip_screening_cost" /></label>
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text">$</span>
                            </div>
                            <input className="form-control" type="number" 
                                value={screeningCost} 
                                onChange={handleNumberInputChange(setScreeningCost)} 
                                min="0" />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <label className='d-flex'>Clinical Assessment Timing <InfoTooltip tooltipTextKey="tooltip_clinical_assessment_timing" /></label>
                        <div className="d-flex align-items-start flex-column justify-content-center">
                            <div className="form-check form-check-inline">
                                <input className="form-check-input" type="radio" name="clinicalAssessmentTiming" id="before" value="before" checked={clinicalAssessmentTiming === 'before'} onChange={(e) => setClinicalAssessmentTiming(e.target.value)} />
                                <label className="form-check-label" htmlFor="before">Before Prescreening</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input className="form-check-input" type="radio" name="clinicalAssessmentTiming" id="after" value="after" checked={clinicalAssessmentTiming === 'after'} onChange={(e) => setClinicalAssessmentTiming(e.target.value)} />
                                <label className="form-check-label" htmlFor="after">After Prescreening</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {areAllVariablesNotNull() && (
                <TooltipProvider>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <RecruitmentBox
                                finalTP={finalTP}
                                prevalence={prevalence}
                                sensitivity={sensitivity}
                                specificity={specificity}
                            />
                        </div>
                        <div className="col-md-3">
                            <ScreeningBox
                                finalTP={finalTP}
                                prevalence={prevalence}
                                sensitivity={sensitivity}
                                specificity={specificity}
                            />
                        </div>
                        <div className="col-md-3">
                            <CostBox
                                clinicalAssessmentCost={clinicalAssessmentCost}
                                prescreeningCost={prescreeningCost}
                                screeningCost={screeningCost}
                                finalTP={finalTP}
                                prevalence={prevalence}
                                sensitivity={sensitivity}
                                specificity={specificity}
                                clinicalAssessmentTiming={clinicalAssessmentTiming}
                            />
                        </div>
                        <div className="col-md-3">
                            <TimeBox
                                finalTP={finalTP}
                                prevalence={prevalence}
                                sensitivity={sensitivity}
                                specificity={specificity}
                            />
                        </div>
                    </div>
                    <div className="card p-3 mb-0">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <h4 className='d-flex'>Sample Size by Method <InfoTooltip tooltipTextKey="tooltip_sample_size_plot" /></h4>
                                <FunnelPlot
                                    sensitivity={sensitivity}
                                    specificity={specificity}
                                    prevalence={prevalence}
                                    finalTP={finalTP}
                                />
                            </div>
                            <div className="col-md-4">
                                <h4 className='d-flex'>Cost Analysis by Method <InfoTooltip tooltipTextKey="tooltip_cost_plot" /></h4>
                                <CostPlot
                                    clinicalAssessmentCost={clinicalAssessmentCost}
                                    prescreeningCost={prescreeningCost}
                                    otherPrescreeningCost={otherPrescreeningCost}
                                    screeningCost={screeningCost}
                                    finalTP={finalTP}
                                    prevalence={prevalence}
                                    sensitivity={sensitivity}
                                    specificity={specificity}
                                    clinicalAssessmentTiming={clinicalAssessmentTiming}
                                />
                            </div>
                            <div className="col-md-4">
                                <h4 className='d-flex'>Projected Recruitment Time <InfoTooltip tooltipTextKey="tooltip_time_plot" /></h4>
                                <TimePlot
                                    finalTP={finalTP}
                                    prevalence={prevalence}
                                    sensitivity={sensitivity}
                                    specificity={specificity}
                                />
                            </div>
                        </div>
                    </div>
                </TooltipProvider>
            )}

            < div className="modal fade" id="modalCustomData" tabIndex="-1" role="dialog" aria-labelledby="exampleModalCenterTitle" aria-hidden="true">
                <div className={
                    dataFrame ? "modal-dialog modal-xxl modal-lg" : "modal-dialog "
                } role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="exampleModalLongTitle">Calculate parameters from custom data</h5>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row g-3 align-items-center">
                                {!dataFrame && (
                                    <div className="col-md-12">
                                        <DragDropCSV onDataFrameChange={handleDataFrameChange} />
                                    </div>
                                )}
                                {dataFrame && (
                                    <>
                                        <div className="col-md-6 d-flex align-items-center">
                                            <label className="form-label mb-0 mr-2" style={{ width: '220px' }}>Desired Sensitivity (conservative threshold):</label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                value={desiredSensitivity}
                                                onChange={handleDesiredSensitivityChange}
                                                min="0"
                                                max="1"
                                                step="0.01"
                                            />
                                        </div>
                                        <div className="col-md-6 d-flex align-items-center">
                                            <label className="form-label mb-0 mr-2" style={{ width: '160px' }}>Desired Specificity (liberal threshold):</label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                value={desiredSpecificity}
                                                onChange={handleDesiredSpecificityChange}
                                                min="0"
                                                max="1"
                                                step="0.01"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            {dataFrame && (
                                <>
                                    <div className="row mt-3">
                                        <div className="col-md-8">
                                            {dataFrame && optimalThresholds && (
                                                <HistogramWithCutoffs
                                                    data={dataFrame}
                                                    cutPointSens={optimalThresholds?.cutPointSens.toFixed(3)}
                                                    cutPointSpec={optimalThresholds?.cutPointSpec.toFixed(3)}
                                                    cutPointYouden={optimalThresholds?.cutPointYouden.toFixed(3)}
                                                />
                                            )}
                                        </div>
                                        <div className="col-md-4">
                                            {rocPoints.length > 0 && (
                                                <RocCurve rocCurve={rocPoints} />
                                            )}
                                        </div>
                                    </div>
                                    <div className="row mt-3 thresholds">
                                        <div className="col-md-4">
                                            <div className="p-3 card gradient1">
                                                <h4>Conservative Threshold</h4>
                                                <div className="card-body">
                                                    <div className="info d-flex flex-column align-items-center">
                                                        <h5>{optimalThresholds?.cutPointSens.toFixed(3)}</h5>
                                                        <h3>{SensitivitySpecificity(optimalThresholds?.cutPointSens)}</h3>
                                                        <button type="button" className="btn btn-lg btn-light mt-3 w-100" data-dismiss="modal" onClick={() => handleThresholdSelection(optimalThresholds?.cutPointSens)}>Select Conservative Threshold</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 card gradient2">
                                                <h4>Intermediate Threshold</h4>
                                                <div className="card-body">
                                                    <div className="info d-flex flex-column align-items-center">
                                                        <h5>{optimalThresholds?.cutPointYouden.toFixed(3)}</h5>
                                                        <h3>{SensitivitySpecificity(optimalThresholds?.cutPointYouden)}</h3>
                                                        <button type="button" className="btn btn-lg btn-light mt-3 w-100" data-dismiss="modal" onClick={() => handleThresholdSelection(optimalThresholds?.cutPointYouden)}>Select Intermediate Threshold</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 card gradient3">
                                                <h4>Liberal Threshold</h4>
                                                <div className="card-body">
                                                    <div className="info d-flex flex-column align-items-center">
                                                        <h5>{optimalThresholds?.cutPointSpec.toFixed(3)}</h5>
                                                        <h3>{SensitivitySpecificity(optimalThresholds?.cutPointSpec)}</h3>
                                                        <button type="button" className="btn btn-lg btn-light mt-3 w-100" data-dismiss="modal" onClick={() => handleThresholdSelection(optimalThresholds?.cutPointSpec)}>Select Conservative Threshold</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            {dataFrame && (
                                <button type="button" className="btn btn-secondary" onClick={
                                    () => {
                                        setDataFrame(null);
                                        setRocPoints([]);
                                    }
                                }>Reset</button>
                            )}
                            <button type="button" className="btn btn-primary" data-dismiss="modal">Return</button>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
}

export default PreScreening;

const selectComponent = (selectedOption, handleChange, selectedPopulation, selectedApoe, getFilteredOptionsFunction, options, setters) => {
    const filteredOptions = getFilteredOptionsFunction(selectedPopulation, selectedApoe);

    const wrappedHandleChange = (selectedOption) => {
        handleChange(selectedOption, options, setters);
    };

    return <Select
        value={selectedOption}
        onChange={wrappedHandleChange}
        options={selectedPopulation ? filteredOptions : []}
        formatGroupLabel={formatGroupLabel}
        styles={{
            control: (provided) => ({
                ...provided,
                borderWidth: '1px',
                borderColor: '#ced4da',
                borderRadius: '0.25rem',
                boxShadow: 'none',
                '&:hover': {
                    borderColor: '#EEEEEE',
                },
                height: '45px',
                minHeight: '45px',
            }),
        }}
        theme={(theme) => ({
            ...theme,
            borderRadius: '0.25rem',
            colors: {
                ...theme.colors,
                primary: '#C9AB77',
                primary25: '#F0E8DB',
            },
        })} />;
}
